import axios from 'axios';

const API_BASE_URL = 'https://apiservices.bookingwhizz.com/api';

/**
 * Member API Service
 * Handles login and registration for members
 */

/**
 * Get Partner API Key from accommodationGroupDetail or environment
 * @param {string} partnerKey - Partner key from accommodationGroupDetail (optional)
 * @returns {string} Partner API Key
 */
const getPartnerApiKey = (partnerKey = null) => {
  // Priority: partnerKey parameter > environment variable
  if (partnerKey) {
    return partnerKey;
  }
  return process.env.REACT_APP_PARTNER_API_KEY || '';
};
/**
 * Login member
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @param {string} credentials.partnerKey - Partner API Key (optional, from accommodationGroupDetail)
 * @returns {Promise<Object>} Login response with user data and token
 */
export const loginMember = async (credentials) => {
  try {
    const { email, password, partnerKey } = credentials;

    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    const response = await axios.post(
      `${API_BASE_URL}/member/login`,
      {
        email,
        password
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Partner-Api-Key': getPartnerApiKey(partnerKey),
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Login failed');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred during login');
    }
  }
};

/**
 * Register new member
 * @param {Object} memberData - Member registration data
 * @param {string} memberData.first_name - First name
 * @param {string} memberData.last_name - Last name
 * @param {string} memberData.email - Email address
 * @param {string} memberData.password - Password
 * @param {string} memberData.nationality - Nationality (ISO code)
 * @param {string} memberData.mobile_code - Mobile country code (e.g., +91)
 * @param {string} memberData.mobile_number - Mobile number
 * @param {string} memberData.partnerKey - Partner API Key (optional, from accommodationGroupDetail)
 * @returns {Promise<Object>} Registration response with user data
 */
export const registerMember = async (memberData) => {
  try {
    const {
      first_name,
      last_name,
      email,
      password,
      nationality,
      mobile_code,
      mobile_number,
      partnerKey
    } = memberData;

    // Validate required fields
    if (!first_name || !last_name || !email || !password || !nationality || !mobile_code || !mobile_number) {
      throw new Error('All fields are required');
    }

    const response = await axios.post(
      `${API_BASE_URL}/member/register`,
      {
        first_name,
        last_name,
        email,
        password,
        nationality,
        mobile_code,
        mobile_number
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Partner-Api-Key': getPartnerApiKey(partnerKey),
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Registration failed');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred during registration');
    }
  }
};

/**
 * Update member profile
 * @param {Object} memberData - Member profile data
 * @param {string} memberData.profileId - Member profile ID (required)
 * @param {string} memberData.email - Email address
 * @param {string} memberData.phoneNumber - Phone number
 * @param {string} memberData.firstName - First name
 * @param {string} memberData.lastName - Last name
 * @param {string} memberData.mobileCode - Mobile country code (e.g., +92)
 * @param {string} memberData.sourceId - Source ID (default: 54)
 * @param {string} memberData.partnerKey - Partner API Key (optional)
 * @returns {Promise<Object>} Update response
 */
export const updateMemberProfile = async (memberData) => {
  try {
    const { email, phoneNumber, firstName, lastName, mobileCode, profileId, sourceId, partnerKey } = memberData;

    if (!profileId) {
      throw new Error('Profile ID is required');
    }

    const response = await axios.post(
      `${API_BASE_URL}/member/update`,
      { email, phoneNumber: (mobileCode || '') + (phoneNumber || ''), firstName, lastName, mobileCode, profileId, sourceId: sourceId || '54' },
      {
        headers: {
          'Content-Type': 'application/json',
          'Partner-Api-Key': getPartnerApiKey(partnerKey),
          'Accept': 'application/json'
        },
        timeout: 30000
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Profile update failed');
    } else if (error.request) {
      throw new Error('Network error: Please check your internet connection');
    } else {
      throw new Error(error.message || 'An error occurred during profile update');
    }
  }
};

